import React from 'react';
import { getInitials, getDisplayContact } from '../../utils/messageHelpers';
import { Conversation } from '../../types/wa_messages';
import { getLatestAnalysis } from '../../utils/analysis';
import { getCategoryStyling } from '../../utils/categoryStyling';

interface ConversationHeaderProps {
  selectedConversationId: string | null;
  selectedConversation: Conversation | null;
}

const ConversationHeader: React.FC<ConversationHeaderProps> = ({
  selectedConversationId,
  selectedConversation,
}) => {
  if (!selectedConversationId || !selectedConversation) {
    return null;
  }

  return (
    <div className="flex items-center gap-4 px-4 py-4 border-b rounded-2xl border-slate-100 bg-white flex-shrink-0 text-left">
      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xl select-none">
        {getInitials(selectedConversation?.pic_name || '', getDisplayContact(selectedConversation?.pic_contact || ''))}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-lg text-slate-900">
          {selectedConversation?.pic_name || getDisplayContact(selectedConversation?.pic_contact || '')}
          {selectedConversation?.xero_direct_contact_name && selectedConversation?.xero_direct_contact_id && (
            <a
              href={`/organizations/${selectedConversation.organization_id}/customers/${selectedConversation.xero_direct_contact_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline text-sm ml-1"
            >
              ({selectedConversation.xero_direct_contact_name})
            </a>
          )}
        </div>
        {/* Context row between name and phone */}
        <div className="text-xs text-slate-600">
          {(selectedConversation.user_id ? 'Internal' : 'Customer')}{' '}
          {selectedConversation.organization_id && selectedConversation.organization_name ? (
            <a
              href={`/organizations/${selectedConversation.organization_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              @{selectedConversation.organization_name}
            </a>
          ) : (
            <>@{selectedConversation.organization_name || ''}</>
          )}
          {selectedConversation?.xero_direct_contact_name && selectedConversation?.xero_direct_contact_id && (
            <>
              {' '}
            </>
          )}
        </div>
        {/* Remove explicit pic_contact display; message items will show channel-specific contact */}
      </div>
      {/* Right info column: selected account badge and overdue summary */}
      <div className="flex flex-col items-end gap-1 ml-4">
        {(() => {
          const extra = selectedConversation?.extra_metadata || {};
          const selectedAccount: string | undefined = extra.selected_account;
          if (!selectedAccount) return null;
          const rawColor = (extra.selected_account_color || '').toString().toLowerCase();
          const colors: Record<string, { bg: string; text: string; border: string }> = {
            green: { bg: 'bg-green-50', text: 'text-green-700', border: 'ring-green-600/20' },
            amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'ring-amber-600/20' },
            yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'ring-yellow-600/20' },
            red: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'ring-rose-600/20' },
            blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'ring-blue-600/20' },
            purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'ring-purple-600/20' },
            gray: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'ring-slate-600/20' }
          };
          const c = colors[rawColor] || colors.gray;
          return (
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} ring-1 ${c.border}`} title="Selected account">
              {selectedAccount}
            </div>
          );
        })()}

        {(() => {
          const customer: any = (selectedConversation as any)?.customer;
          // Prefer direct selected_category; otherwise derive from agent analyses
          let category: string | undefined = customer?.selected_category;
          if (!category && customer?.agent_analyses) {
            try {
              const analysis = getLatestAnalysis(customer.agent_analyses, 'customer_insight');
              category = analysis?.output_json?.selected_category;
            } catch (e) {
              category = undefined;
            }
          }
          if (!category) return null;
          const styling = getCategoryStyling(category);
          return (
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${styling.bg} ${styling.text}`} title="Selected category">
              {category}
            </div>
          );
        })()}

        {(() => {
          const extra = selectedConversation?.extra_metadata || {};
          const customer: any = (selectedConversation as any)?.customer;
          const overdueCount = Number(customer?.invoice_metadata?.overdue_invoice_count ?? extra.overdue_invoice_count ?? 0);
          const overdueAmount = Number(customer?.invoice_metadata?.overdue_receivable ?? extra.overdue_receivable ?? 0);
          if (!overdueCount || overdueCount <= 0) return null;
          return (
            <div className="text-right">
              <div className="font-semibold text-rose-700 text-xs">{overdueCount} (${overdueAmount.toLocaleString()})</div>
              <div className="text-rose-600 text-[10px]">Overdue</div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ConversationHeader; 